import { Component, DestroyRef, effect, inject, OnInit, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PaginatorState } from 'primeng/paginator';
import { Params } from '../../../services/utilis/add-params-service';
import { CustomFilter, DynamicColumn, TableComponent } from '../../../@shared/components/table-component/table-component';
import { GenericPaginated } from '../../../models/generic-paginated';
import { EndRidePhotos, Ride } from '../../../models/ride';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { combineLatest, distinctUntilChanged, filter, map, of, startWith, Subject, switchMap, tap } from 'rxjs';
import { ShowLocationDialog } from '../../../@shared/dialogs/show-location-dialog/show-location-dialog';
import { RideService } from '../../../services/rides/ride-service'
import { NavBadgeService } from '../../../services/nav-badge/nav-badge-service';
import { ShowImagesCarouselDialog } from '../../../@shared/dialogs/show-images-carousel-dialog/show-images-carousel-dialog';
import { ApproveEndRidePhotosForm } from '../../../@shared/forms/rides/approve-end-ride-photos-form/approve-end-ride-photos-form';
import { RejectEndRidePhotosForm } from '../../../@shared/forms/rides/reject-end-ride-photos-form/reject-end-ride-photos-form';
import { RideVerificationDetails } from '../ride-verification-details/ride-verification-details';
import { Model } from '../../../models/model';
import { ModelService } from '../../../services/vehicles/model-service';
import { Card } from 'primeng/card';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { TextareaModule } from 'primeng/textarea';
import { CommonModule } from '@angular/common';
import { OperationSubmissionList } from '../operation-submission-list/operation-submission-list';
import { OPERATION_TAB } from '../../../constants/ride-verification.constants';
import { OperationSubmissionService } from '../../../services/operation-submissions/operation-submission-service';
import { ReviewStreamService } from '../../../services/review-stream/review-stream-service';

// activeTab value for the operation section; otherwise it holds the ride photo status
const OPERATION_SECTION = 'OPERATION';

@Component({
  selector: 'app-ride-verification-list',
  imports: [TableComponent, ShowLocationDialog, ShowImagesCarouselDialog, ApproveEndRidePhotosForm, RejectEndRidePhotosForm, Card, RideVerificationDetails, SelectModule, FormsModule, TextareaModule, CommonModule, OperationSubmissionList],
  templateUrl: './ride-verification-list.html',
  styleUrl: './ride-verification-list.scss'
})
export class RideVerificationList implements OnInit {

  params = [
    "page",
    "size",
    "search",
    'sortBy',
    'sortDirection',
    'startedFrom',
    'startedTo',
    'status',
    'minRating',
    'maxRating',
    'photoApprovalStatus',
    'modelId',
    'tab'
  ]

  modifiedParams: Params = {
    page: "page",
    size: "size",
    search: "search",
    sortBy: "sortBy",
    sortDirection: "sortDirection",
    startedFrom: "startedFrom",
    startedTo: "startedTo",
    status: "status",
    minRating: "minRating",
    maxRating: "maxRating",
    photoApprovalStatus: "photoApprovalStatus",
    modelId: "modelId",
  }

  // Define columns to match Figma screenshot
  ridesTableColumns: DynamicColumn<Ride>[] = [
    { header: 'ID', field: 'rideId', sortable: true },
    { header: 'Customer', field: 'customerFirstName', mergedName: 'customerLastName', bottomField: 'customerEmail', isAvatar: true, sortable: true },
    { header: 'Phone Number', field: 'customerPhoneNumber', sortable: false },
    { header: 'Started At', field: 'startedAt', sortable: true, isDateTime: true },
    {
      header: 'Plate Number',
      field: 'plateNo',
      sortable: false,
      command: (ride) => this.redirectToVehiclesPage(ride),
      cellClass: 'text-primary underline cursor-pointer',
    },
    { header: 'Distance (KM)', field: 'distanceKm', sortable: false },
    { header: 'Duration', field: 'durationSeconds', sortable: false, isDuration: true },
    {
      field: 'actions',
      header: 'End Location',
      sortable: false,
      actions: [
        {
          isLocationButton: true,
          command: (ride) => this.openLocationDialog(ride),
          tooltip: 'Show Location',
        },
      ],
    },
    {
      header: 'Photo Status',
      field: 'photoApprovalStatus',
      sortable: false,
      isStatusBadge: true,
      cellClass: 'cursor-pointer',
      command: (ride) => this.openChangeStatusDialog(ride),
      badgeStyleGetter: (value) => {
        const map: Record<string, { background: string; color: string }> = {
          'APPROVED': { background: 'var(--status-available-bg, #dcfce7)', color: 'var(--status-available-text, #16a34a)' },
          'REJECTED': { background: 'var(--status-rejected-bg, #fee2e2)', color: 'var(--status-rejected-text, #dc2626)' },
          'PENDING': { background: 'var(--status-pending-bg, #fef9c3)', color: 'var(--status-pending-text, #854d0e)' },
        };
        return map[value as string] ?? { background: 'transparent', color: 'inherit' };
      },
    },
    {
      field: 'actions',
      header: 'Parking Photo',
      sortable: false,
      actions: [
        {
          isTextButton: true,
          text: 'SHOW',
          command: (ride) => this.getEndRidePhotosForDetails(ride),
          tooltip: 'View parking photos',
        },
      ],
    },
    {
      header: 'Feedback',
      field: 'feedbackRating',
      sortable: false,
      isRating: true,
      isFilterable: true,
      filterType: 'slider',
      sliderMinKey: 'minRating',
      sliderMaxKey: 'maxRating',
      min: 1,
      max: 5,
      step: 1,
    },
    { header: 'Verification Comment', field: 'photoReviewComment', sortable: false },
  ];

  rides = signal<Ride[]>([]);
  totalRecords = signal(0);
  pendingCount = signal(0);
  approvedCount = signal(0);
  rejectedCount = signal(0);
  operationPendingCount = signal(0);
  operationApprovedCount = signal(0);
  operationRejectedCount = signal(0);  first = 0;
  rows = 20;
  activeTab: string | null = null;
  endRidePhotos = signal<EndRidePhotos | undefined>(undefined)
  selectedRide: Ride
  selectedRideIndex = 0
  private ridesCache = new Map<string, GenericPaginated<Ride>>();
  private lastNonTabParams = '';
  // Re-runs the ride list query with the current params (after a review or a live count change)
  private ridesRefresh$ = new Subject<void>();

  searchControl = new FormControl('');
  changeStatusDialog = false
  changeStatusRide: Ride | null = null
  changeStatusValue: string | null = null
  changeStatusReason = ''
  changeStatusLoading = false
  changeStatusError = ''

  readonly statusOptions = [
    { label: 'Pending', value: 'PENDING' },
    { label: 'Approved', value: 'APPROVED' },
    { label: 'Rejected', value: 'REJECTED' },
  ]

  locationDialog = false
  imagesCarouselDialog = false
  detailsDialog = false
  detailsShowNav = false
  rejectEndRidePhotosDialog = false
  approveEndRidePhotosDialog = false

  rideStatusFilter: CustomFilter = {
    key: "status",
    label: "Ride Status",
    type: "multiselect",
    options: [
      { label: 'Completed', value: 'COMPLETED' },
      { label: 'Completed With Debt', value: 'COMPLETED_WITH_DEBT' },
    ]
  }

  startedDateFilter: CustomFilter = {
    key: "started",
    label: "Started Date",
    type: "date-range",
    options: []
  }

  modelsFilter: CustomFilter = {
    key: "modelId",
    label: "Model",
    type: "multiselect",
    options: []
  }

  private rideService = inject(RideService)
  private modelService = inject(ModelService)
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)
  private navBadgeService = inject(NavBadgeService)
  private operationSubmissionService = inject(OperationSubmissionService)
  private reviewStream = inject(ReviewStreamService)
  private destroyRef = inject(DestroyRef)

  constructor() {
    // Refresh operation counts whenever the live pending count changes (also runs once on load)
    effect(() => {
      this.reviewStream.operationSubmissionCount();
      untracked(() => this.loadOperationStats());
    });

    // Same for ride photo counts; after the first run also reload the ride list
    let ridesInitialized = false;
    effect(() => {
      this.reviewStream.verificationCount();
      untracked(() => {
        this.loadPendingCount();
        if (ridesInitialized) this.refreshRides();
        ridesInitialized = true;
      });
    });
  }

  ngOnInit() {
    this.getModels();

    const currentParams = this.activatedRoute.snapshot.queryParams;
    if (!currentParams['photoApprovalStatus'] && currentParams['tab'] !== OPERATION_TAB) {
      this.router.navigate([], {
        queryParams: { photoApprovalStatus: 'PENDING', page: 0 },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }


        this.activatedRoute.queryParams.subscribe(params => {
          this.activeTab = params['tab'] === OPERATION_TAB
            ? OPERATION_SECTION
            : params['photoApprovalStatus'] ?? 'PENDING';
        });
    this.getRideVerificationsList();
  }

  get isOperationTab(): boolean {
    return this.activeTab === OPERATION_SECTION;
  }

  // Ride and operation sections use different filters, so switching starts clean
  setSection(section: 'ride' | 'operation') {
    if ((section === 'operation') === this.isOperationTab) return;
    this.detailsDialog = false;
    this.router.navigate([], {
      queryParams: section === 'operation'
        ? { tab: OPERATION_TAB, status: 'PENDING', page: 0, size: 20 }
        : { photoApprovalStatus: 'PENDING', page: 0, size: 20 },
    });
  }

  setTab(status: string | null) {
    this.activeTab = status;
    if (status !== 'PENDING') {
      this.detailsDialog = false;
    }
    this.router.navigate([], {
      queryParams: { photoApprovalStatus: status ?? undefined, page: 0, size: 20 },
      queryParamsHandling: 'merge',
    });
  }

  loadOperationStats() {
    this.operationSubmissionService.getStatistics()
      .subscribe(res => {
        this.operationPendingCount.set(res.data.pending);
        this.operationApprovedCount.set(res.data.approved);
        this.operationRejectedCount.set(res.data.rejected);
      });
  }

  loadPendingCount() {
    this.rideService.getVerificationStats()
      .subscribe(res => {
        this.pendingCount.set(res.data.pending);
        this.approvedCount.set(res.data.approved);
        this.rejectedCount.set(res.data.rejected);
      });
  }

  // Drops the cached tabs and refetches the current page
  refreshRides() {
    this.ridesCache.clear();
    this.ridesRefresh$.next();
  }

  getRideVerificationsList = () => {
    const queryParams$ = this.activatedRoute.queryParams.pipe(
      map((params) =>
        Object.fromEntries(
          Object.entries(params)
            .filter(([key]) => this.params.includes(key))
            .map(([key, value]) => [this.modifiedParams[key] || key, value])
        )
      ),

      distinctUntilChanged((previous, current) =>
        JSON.stringify(previous) === JSON.stringify(current)
      ),
    );

    combineLatest([queryParams$, this.ridesRefresh$.pipe(startWith(undefined))]).pipe(
      map(([queryParams]) => queryParams),

      // The operation tab loads its own list
      filter((queryParams) => queryParams['tab'] !== OPERATION_TAB),

      tap((queryParams) => {
        this.searchControl.setValue(queryParams['search'], { emitEvent: false });
        this.rows = queryParams['size'] ? +queryParams['size'] : 20;
      }),
      switchMap((queryParams) => {
        const tab = queryParams['photoApprovalStatus'] ?? 'PENDING';

        // Build a key from everything except the tab so we can detect real filter changes
        const nonTabParams = { ...queryParams };
        delete nonTabParams['photoApprovalStatus'];
        const nonTabKey = JSON.stringify(nonTabParams);

        // If non-tab filters changed, invalidate the whole cache
        if (nonTabKey !== this.lastNonTabParams) {
          this.ridesCache.clear();
          this.lastNonTabParams = nonTabKey;
        }

        // Return cached data for this tab if available
        if (this.ridesCache.has(tab)) {
          return of(this.ridesCache.get(tab) as GenericPaginated<Ride>);
        }

        const params: Record<string, string | number> = {
          ...queryParams,
          status: queryParams['status'] ?? 'COMPLETED,COMPLETED_WITH_DEBT',
          sortBy: queryParams['sortBy'] ?? 'startedAt',
          sortDirection: queryParams['sortDirection'] ?? 'DESC',
          photoApprovalStatus: tab,
        };
        return this.rideService.getRidesList(params).pipe(
          tap(res => this.ridesCache.set(tab, res))
        );
      }),
      takeUntilDestroyed(this.destroyRef),
    )
      .subscribe((res) => {
        this.rides.set(res.data.content);
        this.totalRecords.set(res.data.totalElements)
        this.first = ((res.data.currentPage + 1) * res.data.pageSize) - res.data.pageSize

        // Auto-open first ride when Pending tab is active,
        // but keep the ride under review if a live refresh still lists it
        if (this.activeTab === 'PENDING' && res.data.content.length > 0) {
          const current = this.detailsDialog && this.detailsShowNav
            ? res.data.content.find(r => r.rideId === this.selectedRide?.rideId)
            : undefined;
          this.getEndRidePhotosForDetails(current ?? res.data.content[0], true);
        }
      });
  }

  getModels() {
    this.modelService.getModelsForDropdown().subscribe({
      next: (res) => {
        const options = res.data.map((m: Model) => ({ label: m.name, value: m.id }));
        this.modelsFilter.options = options;
      }
    });
  }

  onPageChange = (event: PaginatorState) => {
    const queryparams = {
      page: event.page,
      size: event.rows,
    }
    this.router.navigate([], {
      queryParams: queryparams,
      queryParamsHandling: 'merge'
    })
  }

  openLocationDialog = (selectedRow: Ride) => {
    this.selectedRide = selectedRow

    const url = `https://www.google.com/maps/search/?api=1&query=${selectedRow.telemetryEndLat},${selectedRow.telemetryEndLng}`;
    window.open(url, '_blank');
  }

  getEndRidePhotos = (ride: Ride) => {
    this.rideService.getEndRidePhotos(ride.rideId).subscribe({
      next: (res) => {
        this.endRidePhotos.set(res.data)
        this.imagesCarouselDialog = true
      }
    })
  }

  openImagesCarouselDialog = (ride: Ride) => {
    this.selectedRide = ride
    this.imagesCarouselDialog = false
    this.detailsDialog = false
    this.getEndRidePhotos(ride)
  }

  getEndRidePhotosForDetails = (ride: Ride, showNav = false) => {
    this.selectedRide = ride
    this.selectedRideIndex = this.rides().findIndex(r => r.rideId === ride.rideId)
    this.rideService.getEndRidePhotos(ride.rideId).subscribe({
      next: (res) => {
        this.endRidePhotos.set(res.data)
        this.detailsShowNav = showNav
        this.detailsDialog = true
      }
    })
  }

  onDetailsPrev = () => {
    const idx = this.selectedRideIndex - 1;
    if (idx >= 0) {
      this.selectedRide = this.rides()[idx];
      this.getEndRidePhotosForDetails(this.selectedRide, this.detailsShowNav);
    }
  }

  onDetailsNext = () => {
    const idx = this.selectedRideIndex + 1;
    if (idx < this.rides().length) {
      this.selectedRide = this.rides()[idx];
      this.getEndRidePhotosForDetails(this.selectedRide, this.detailsShowNav);
    }
  }

  openApproveEndRidePhotos = () => {
    this.imagesCarouselDialog = false
    this.approveEndRidePhotosDialog = true
  }

  onEndRidePhotosApproved = () => {
    this.refreshRides()
    this.loadPendingCount()
    this.navBadgeService.refresh()
    this.approveEndRidePhotosDialog = false
  }

  openRejectEndRidePhotos = () => {
    this.imagesCarouselDialog = false
    this.rejectEndRidePhotosDialog = true
  }

  onEndRidePhotosRejected = () => {
    this.refreshRides()
    this.loadPendingCount()
    this.navBadgeService.refresh()
    this.rejectEndRidePhotosDialog = false
  }

  get ridePhotos(): string[] {
    return [
      this.endRidePhotos()!.parkingBoardPhotoUrl
    ].filter(Boolean);
  }

  redirectToVehiclesPage = (ride: Ride) => {
    this.router.navigate(['/vehicles'], {
      queryParams: { search: ride.plateNo },
      queryParamsHandling: ''
    });
  }

  openChangeStatusDialog = (ride: Ride) => {
    this.changeStatusRide = ride
    this.changeStatusValue = ride.photoApprovalStatus ?? null
    this.changeStatusReason = ride.photoReviewComment ?? ''
    this.changeStatusError = ''
    this.changeStatusLoading = false
    this.changeStatusDialog = true
  }

  submitChangeStatus = () => {
    if (!this.changeStatusRide || !this.changeStatusValue) return
    this.changeStatusLoading = true
    this.changeStatusError = ''
    const payload: { status: string; comment?: string } = { status: this.changeStatusValue }
    if (this.changeStatusReason.trim()) {
      payload.comment = this.changeStatusReason.trim()
    }
    this.rideService.updatePhotoStatus(this.changeStatusRide.rideId, payload).subscribe({
      next: () => {
        this.changeStatusLoading = false
        this.changeStatusDialog = false
        this.loadPendingCount()
        this.navBadgeService.refresh()
        this.refreshRides()
      },
      error: () => {
        this.changeStatusLoading = false
        this.changeStatusError = 'Failed to update status. Please try again.'
      }
    })
  }

  redirectToCustomersPage = (ride: Ride) => {
    this.router.navigate(['/customers'], {
      queryParams: { search: ride.customerEmail },
      queryParamsHandling: ''
    });
  }
}
